<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { Notify, useDialogPluginComponent } from 'quasar';
import { useI18n } from 'vue-i18n';

import DocumentSubmissionDecisionPanel from '@/components/dialogs/DocumentSubmissionDecisionPanel.vue';
import DocumentSubmissionVersionHistory from '@/components/dialogs/DocumentSubmissionVersionHistory.vue';
import SubmissionMetaGrid from '@/components/dialogs/SubmissionMetaGrid.vue';
import { Constants } from '@/constants';
import { getStateLabel } from '@/helpers/documentSubmission.helper';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { getFullName } from '@/helpers/text.helper';
import type {
  DocumentSubmissionDetailDto,
  DocumentSubmissionPublishDto,
  DocumentSubmissionRefuseDto,
  Locales,
} from '@/services/types/api.types';
import { useDocumentSubmissionsStore } from '@/stores/documentSubmissions.store';

// PROPS
const props = defineProps<{
  documentSubmissionId: number;
  languageEnabled: Locales[];
}>();

// EMITS
defineEmits([...useDialogPluginComponent.emits]);

// CONSTS
const { t } = useI18n();
const documentSubmissionsStore = useDocumentSubmissionsStore();
// The dialog has no OK: it only closes, and the list reloads on dismiss.
const { dialogRef, onDialogHide, onDialogCancel } = useDialogPluginComponent();

// REFS
const documentSubmission = ref<DocumentSubmissionDetailDto | null>(null);
const loading = ref(true);
const selectedVersionId = ref<number | null>(null);
const saving = ref(false);

// COMPUTED
const selectedVersion = computed(
  () => documentSubmission.value?.versions.find((version) => version.id === selectedVersionId.value) ?? null,
);

const publishedLocales = computed(() => {
  const { versions, published_version_id: publishedVersionId } = documentSubmission.value ?? {};
  return versions?.find((version) => version.id === publishedVersionId)?.published_locales ?? null;
});

const metaItems = computed(() => {
  if (!documentSubmission.value) return [];
  const { user, state, versions, published_version_id: publishedVersionId } = documentSubmission.value;
  const latestVersionNumber = versions[0]?.version_number;
  const publishedVersion = versions.find((version) => version.id === publishedVersionId);
  return [
    { label: t('project-module.documentSubmission.participant'), value: getFullName(user) },
    { label: t('global.label.email'), value: user?.email },
    { label: t('global.label.status'), value: getStateLabel(state) },
    {
      label: t('project-module.documentSubmission.dialogs.version'),
      value: latestVersionNumber
        ? t('project-module.documentSubmission.dialogs.versionOf', {
            n: latestVersionNumber,
            m: Constants.ProjectModule.DocumentSubmission.Upload.MaxVersionsPerDocument,
          })
        : null,
    },
    {
      label: t('project-module.documentSubmission.dialogs.publicVersion'),
      value: publishedVersion ? `v${publishedVersion.version_number}` : null,
    },
  ];
});

// FUNCTIONS
// Keeps the selected version when it is still in the history, the latest one otherwise.
const showDocument = (document: DocumentSubmissionDetailDto | null) => {
  documentSubmission.value = document;
  if (!document?.versions.some((version) => version.id === selectedVersionId.value)) {
    selectedVersionId.value = document?.latest_version_id ?? document?.versions[0]?.id ?? null;
  }
};

const fetchDocument = () => documentSubmissionsStore.fetchDocumentSubmissionById(props.documentSubmissionId);

const isConflict = (error: unknown) => (error as { response?: { status?: number } })?.response?.status === 409;

/**
 * Runs a decision and shows the document the API answers. A 409 means the document changed since the dialog loaded
 * it, for instance a new version arrived: the dialog reloads it and says so, in place of the generic error. When that
 * reload fails too, the dialog keeps what it shows and asks for a reopen. It stays open either way, and the list
 * reloads once it closes.
 */
const decide = (call: () => Promise<DocumentSubmissionDetailDto>) => {
  saving.value = true;
  return wrapSavingOperation(async () => {
    try {
      showDocument(await call());
    } catch (error) {
      if (!isConflict(error)) throw error;
      const reloaded = await fetchDocument();
      if (reloaded) showDocument(reloaded);
      Notify.create({
        color: 'warning',
        message: t(
          reloaded
            ? 'project-module.documentSubmission.dialogs.decision.conflict'
            : 'project-module.documentSubmission.dialogs.decision.conflictNotReloaded',
        ),
        position: 'top',
        timeout: 4000,
      });
    } finally {
      saving.value = false;
    }
    return true;
  });
};

const onPublish = (versionId: number, decision: DocumentSubmissionPublishDto) =>
  decide(() => documentSubmissionsStore.publishVersion(versionId, decision));

const onRefuse = (versionId: number, decision: DocumentSubmissionRefuseDto) =>
  decide(() => documentSubmissionsStore.refuseVersion(versionId, decision));

const onUnpublish = (documentSubmissionId: number) =>
  decide(() => documentSubmissionsStore.unpublishDocument(documentSubmissionId));

// LIFECYCLE
// The list row carries no history, so the dialog loads the document itself.
onMounted(async () => {
  showDocument(await fetchDocument());
  loading.value = false;
});
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" class="document-submission-dialog">
    <q-card class="q-dialog-plugin document-submission-dialog__card" style="width: min(800px, 96vw); max-width: 96vw">
      <q-card-section class="row">
        <div class="text-h6">{{ t('project-module.documentSubmission.dialogs.title') }}</div>
        <q-space />
      </q-card-section>
      <q-separator />
      <q-card-section class="document-submission-dialog__content">
        <div v-if="loading" class="row justify-center q-pa-lg">
          <q-spinner size="2em" color="primary" />
        </div>
        <div v-else-if="!documentSubmission" class="text-negative">
          {{ t('project-module.documentSubmission.dialogs.loadError') }}
        </div>
        <template v-else>
          <submission-meta-grid class="q-mb-lg" :items="metaItems" />
          <div class="text-subtitle1 q-mb-sm">{{ t('project-module.documentSubmission.dialogs.history') }}</div>
          <document-submission-version-history
            :versions="documentSubmission.versions"
            :published-version-id="documentSubmission.published_version_id"
            v-model:selected-version-id="selectedVersionId"
          />
        </template>
      </q-card-section>
      <q-separator />
      <document-submission-decision-panel
        v-if="documentSubmission && selectedVersion"
        :version="selectedVersion"
        :can-unpublish="documentSubmission.canUnpublish"
        :published-locales="publishedLocales"
        :language-enabled="props.languageEnabled"
        :busy="saving"
        @publish="onPublish(selectedVersion.id, $event)"
        @refuse="onRefuse(selectedVersion.id, $event)"
        @unpublish="onUnpublish(documentSubmission.id)"
      >
        <q-btn color="primary" flat :label="t('global.close')" @click="onDialogCancel" />
      </document-submission-decision-panel>
      <q-card-section v-else class="row">
        <q-space />
        <q-card-actions>
          <q-btn color="primary" flat :label="t('global.close')" @click="onDialogCancel" />
        </q-card-actions>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<style lang="scss">
.document-submission-dialog {
  &__card {
    max-height: calc(100vh - 48px);
    display: flex;
    flex-direction: column;
  }

  &__content {
    overflow-y: auto;
  }
}
</style>
